import { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useAuthStore } from '@/stores/authStore';
import { useDashboardStats } from '@/hooks';
import MetricCard from '@/components/MetricCard/MetricCard';
import { DateRangePicker } from '@/components/DateRangePicker';
import {
  AdvertiserPerformance,
  DailyAdvertiserPerformance,
  RateLimitMonitor,
  FeatureCards,
  QuickActions,
} from '@/features/dashboard';

const DashboardPage = () => {
  const { user } = useAuthStore();

  // Date range state - defaults to last 8 days (to match old dashboard)
  const getDefaultDates = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const start = new Date(yesterday);
    start.setDate(start.getDate() - 7); // 8 days ago (to get 8-day range ending yesterday)

    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDate(start),
      to: formatDate(yesterday),
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDates());
  const [selectedAdvertiser, setSelectedAdvertiser] = useState<string>('');

  // Fetch dashboard stats with date range and advertiser filter
  const { data: stats, loading, error, refetch } = useDashboardStats({
    immediate: true,
    params: {
      fromDate: dateRange.from,
      toDate: dateRange.to,
      advertiser: selectedAdvertiser || undefined,
    },
  });

  // Handle advertiser filter change
  const handleAdvertiserChange = (event: SelectChangeEvent<string>) => {
    setSelectedAdvertiser(event.target.value);
  };

  // Manual refresh
  const handleRefresh = () => {
    refetch();
  };

  // Format currency for display
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Mock advertisers list - TODO: fetch from API later
  const advertisers = [
    '1G TapStone RSOC FB N2S',
    '1G Inuvo N2S RSOC PRISM FB',
    '1G Yahoo Bing AirFind RSOC',
  ];

  return (
    <Box>
      {/* Header with integrated filters */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={3}
        >
          {/* Left side - Title */}
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome back, {user?.name || user?.email}!
            </Typography>
          </Box>

          {/* Right side - Filters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            {/* Date Range Picker */}
            <Box>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                placeholder="Select date range"
              />
            </Box>

            {/* Advertiser Filter */}
            <FormControl
              size="small"
              sx={{
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  height: 40,
                },
                '& .MuiSelect-select': {
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              <InputLabel>Advertiser</InputLabel>
              <Select
                value={selectedAdvertiser}
                label="Advertiser"
                onChange={handleAdvertiserChange}
              >
                <MenuItem value="">All Advertisers</MenuItem>
                {advertisers.map((advertiser) => (
                  <MenuItem key={advertiser} value={advertiser}>
                    {advertiser}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Refresh button */}
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              size="medium"
              sx={{
                width: 40,
                height: 40,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load dashboard data: {error}
        </Alert>
      )}

      {/* Dashboard Cards - Always mounted with opacity transition */}
      <Box
        sx={{
          opacity: loading ? 0.5 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: loading ? 'none' : 'auto',
        }}
      >
        {stats && (
          <Grid container spacing={3}>
            {/* Revenue */}
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="Revenue"
                value={formatCurrency(stats.revenue)}
                color="success"
                icon={<AttachMoneyIcon />}
              />
            </Grid>

            {/* Spend */}
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="Spend"
                value={formatCurrency(stats.todaySpend)}
                color="error"
                icon={<PaymentsIcon />}
              />
            </Grid>

            {/* P&L */}
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="P&L"
                value={stats.profitLossFormatted}
                color={stats.profitLoss >= 0 ? 'success' : 'error'}
                icon={<AccountBalanceIcon />}
              />
            </Grid>

            {/* ROAS */}
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="ROAS"
                value={stats.roasFormatted}
                color={stats.roas >= 100 ? 'success' : 'error'}
                icon={<ShowChartIcon />}
              />
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Advertiser Performance Trends Chart - Always mounted */}
      <Box sx={{ mt: 4 }}>
        <AdvertiserPerformance
          selectedAdvertiser={selectedAdvertiser}
          dateRange={dateRange}
        />
      </Box>

      {/* Daily Advertiser Performance Summary Table - Always mounted */}
      <Box sx={{ mt: 4 }}>
        <DailyAdvertiserPerformance
          startDate={new Date(dateRange.from)}
          endDate={new Date(dateRange.to)}
          selectedAdvertiser={selectedAdvertiser}
        />
      </Box>

      {/* Rate Limit Monitor */}
      <Box sx={{ mt: 4 }}>
        <RateLimitMonitor />
      </Box>

      {/* Feature Cards */}
      <Box sx={{ mt: 4 }}>
        <FeatureCards />
      </Box>

      {/* Quick Actions */}
      <Box sx={{ mt: 4 }}>
        <QuickActions />
      </Box>
    </Box>
  );
};

export default DashboardPage;
